import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { diagnose, type Diagnostics } from '@oolio-group/pay-embedded';
import { useTranslation } from '@oolio-group/localization';
import { styles } from './Tap2PayDiagnosis.styles';
import Tap2PayDiagnosisRow from './Tap2PayDiagnosisRow';
import ModalTitle from '../../System/ModalTitle/ModalTitle';
import Sentry from '../../../utils/Sentry/Sentry';
import { ButtonText } from '../../Button/ButtonText';
import {
  ensureDeviceVirtualTerminal,
  getStoredVirtualTerminal,
  userIdFromToken,
  type StoredVirtualTerminal,
} from '../../../utils/tap2PayVirtualTerminal';

interface Props {
  onClose: () => void;
  payConfig: {
    merchantCode?: string;
    merchantId?: string;
    storeRef?: string;
    deviceId?: string;
    accessToken?: string;
  };
}

const Tap2PayDiagnosis: React.FC<Props> = ({ onClose, payConfig }) => {
  const { translate } = useTranslation();
  const [diagnostics, setDiagnostics] = useState<Diagnostics>();
  const [failureReason, setFailureReason] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [virtualTerminal, setVirtualTerminal] =
    useState<StoredVirtualTerminal>();
  const [creatingVirtualTerminal, setCreatingVirtualTerminal] = useState(false);
  const [virtualTerminalError, setVirtualTerminalError] = useState<string>();

  const userId = userIdFromToken(payConfig.accessToken);
  const canCreateVirtualTerminal = Boolean(
    payConfig.deviceId &&
      payConfig.merchantId &&
      payConfig.storeRef &&
      payConfig.accessToken &&
      userId,
  );

  useEffect(() => {
    let active = true;
    getStoredVirtualTerminal({
      deviceId: payConfig.deviceId,
      merchant: payConfig.merchantId,
      store: payConfig.storeRef,
    }).then(saved => {
      if (active) setVirtualTerminal(saved);
    });
    return () => {
      active = false;
    };
  }, [payConfig.deviceId, payConfig.merchantId, payConfig.storeRef]);

  const onCreateVirtualTerminal = useCallback(async () => {
    if (!canCreateVirtualTerminal) return;
    setCreatingVirtualTerminal(true);
    setVirtualTerminalError(undefined);
    try {
      const result = await ensureDeviceVirtualTerminal({
        deviceId: payConfig.deviceId as string,
        merchant: payConfig.merchantId as string,
        store: payConfig.storeRef as string,
        accessToken: payConfig.accessToken as string,
        userId,
        trigger: 'diagnosis',
        force: true,
      });
      setVirtualTerminal(result);
    } catch (e: unknown) {
      Sentry.captureException(e, {
        extra: { context: 'tap2pay-virtual-terminal-diagnosis-cds' },
      });
      setVirtualTerminalError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingVirtualTerminal(false);
    }
  }, [canCreateVirtualTerminal, payConfig, userId]);

  useEffect(() => {
    let active = true;
    diagnose()
      .then(result => {
        if (active) setDiagnostics(result);
      })
      .catch((e: unknown) => {
        Sentry.captureException(e, {
          extra: { context: 'tap2pay-diagnose-cds' },
        });
        if (active) {
          setFailureReason(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const expiry = useMemo(() => {
    const sdk = diagnostics?.sdk;
    if (!sdk?.expiresAt) return undefined;
    if (sdk.expired) {
      return `${sdk.expiresAt} (${translate('tap2PayDiagnosis.expired')})`;
    }
    if (sdk.expiresInDays === undefined) return sdk.expiresAt;
    return `${sdk.expiresAt} (${translate('tap2PayDiagnosis.expiresInDays', {
      count: sdk.expiresInDays,
    })})`;
  }, [diagnostics?.sdk, translate]);

  const permissionList = (values: string[]) =>
    values.length > 0 ? values.join(', ') : translate('tap2PayDiagnosis.none');

  return (
    <View style={styles.container}>
      <ModalTitle
        title={translate('tap2PayDiagnosis.header')}
        onDismiss={onClose}
      />
      <View style={styles.content}>
        <ScrollView style={styles.scroll}>
          {loading ? (
            <View style={[styles.banner, styles.bannerNeutral]}>
              <Text testID="diagnosis-loading" style={styles.bannerText}>
                {translate('tap2PayDiagnosis.running')}
              </Text>
            </View>
          ) : null}

          {diagnostics ? (
            <View
              style={[
                styles.banner,
                diagnostics.ready
                  ? styles.bannerPositive
                  : styles.bannerNegative,
              ]}
            >
              <Text testID="diagnosis-summary" style={styles.bannerText}>
                {diagnostics.summary}
              </Text>
            </View>
          ) : null}

          {failureReason ? (
            <View style={[styles.banner, styles.bannerNegative]}>
              <Text testID="diagnosis-error" style={styles.bannerText}>
                {`${translate('tap2PayDiagnosis.failed')} — ${failureReason}`}
              </Text>
            </View>
          ) : null}

          {diagnostics?.errorMessage &&
          diagnostics.errorMessage !== diagnostics.summary ? (
            <View style={[styles.banner, styles.bannerNegative]}>
              <Text testID="diagnosis-native-error" style={styles.bannerText}>
                {diagnostics.errorMessage}
              </Text>
            </View>
          ) : null}

          <Text style={styles.sectionTitle}>
            {translate('tap2PayDiagnosis.sectionPayConfig')}
          </Text>
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.merchantCode')}
            value={payConfig.merchantCode}
          />
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.merchantId')}
            value={payConfig.merchantId}
          />
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.storeRef')}
            value={payConfig.storeRef}
          />

          <Text style={styles.sectionTitle}>
            {translate('tap2PayDiagnosis.sectionVirtualTerminal')}
          </Text>
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.deviceId')}
            value={payConfig.deviceId}
          />
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.virtualTerminalId')}
            value={virtualTerminal?.terminalId}
          />
          <Tap2PayDiagnosisRow
            label={translate('tap2PayDiagnosis.labels.virtualTerminalSerial')}
            value={virtualTerminal?.serialNumber}
          />
          {virtualTerminal?.action ? (
            <Tap2PayDiagnosisRow
              label={translate('tap2PayDiagnosis.labels.virtualTerminalResult')}
              value={virtualTerminal.action}
            />
          ) : null}
          {virtualTerminalError ? (
            <View style={[styles.banner, styles.bannerNegative]}>
              <Text
                testID="diagnosis-virtual-terminal-error"
                style={styles.bannerText}
              >
                {`${translate(
                  'tap2PayDiagnosis.virtualTerminalFailed',
                )} — ${virtualTerminalError}`}
              </Text>
            </View>
          ) : null}
          <ButtonText
            id="diagnosis-create-virtual-terminal"
            label={translate(
              creatingVirtualTerminal
                ? 'tap2PayDiagnosis.creatingVirtualTerminal'
                : 'tap2PayDiagnosis.createVirtualTerminal',
            )}
            loading={creatingVirtualTerminal}
            disabled={!canCreateVirtualTerminal || creatingVirtualTerminal}
            containerStyles={styles.action}
            onPress={onCreateVirtualTerminal}
          />

          {diagnostics ? (
            <>
              <Text style={styles.sectionTitle}>
                {translate('tap2PayDiagnosis.sectionSdk')}
              </Text>
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.initialized')}
                value={diagnostics.sdk.initialized}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.tapToPayAvailable')}
                value={diagnostics.sdk.tapToPayAvailable}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.installationId')}
                value={diagnostics.sdk.installationId}
              />
              {diagnostics.sdk.version ? (
                <Tap2PayDiagnosisRow
                  label={translate('tap2PayDiagnosis.labels.version')}
                  value={diagnostics.sdk.version}
                />
              ) : null}
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.expiresAt')}
                value={expiry}
              />
              {diagnostics.sdk.unconfirmedBatchCount !== undefined ? (
                <Tap2PayDiagnosisRow
                  label={translate(
                    'tap2PayDiagnosis.labels.unconfirmedBatchCount',
                  )}
                  value={diagnostics.sdk.unconfirmedBatchCount}
                />
              ) : null}
              {diagnostics.sdk.result ? (
                <Tap2PayDiagnosisRow
                  label={translate('tap2PayDiagnosis.labels.result')}
                  value={diagnostics.sdk.result}
                />
              ) : null}
              {diagnostics.sdk.errorCondition ? (
                <Tap2PayDiagnosisRow
                  label={translate('tap2PayDiagnosis.labels.errorCondition')}
                  value={diagnostics.sdk.errorCondition}
                />
              ) : null}

              <Text style={styles.sectionTitle}>
                {translate('tap2PayDiagnosis.sectionPermissions')}
              </Text>
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.granted')}
                value={permissionList(diagnostics.permissions.granted)}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.denied')}
                value={permissionList(diagnostics.permissions.denied)}
                isError={diagnostics.permissions.denied.length > 0}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.blocking')}
                value={permissionList(diagnostics.permissions.blocking)}
              />

              <Text style={styles.sectionTitle}>
                {translate('tap2PayDiagnosis.sectionDevice')}
              </Text>
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.nfcHardware')}
                value={diagnostics.device.nfcHardware}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.nfcEnabled')}
                value={diagnostics.device.nfcEnabled}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.hostReachable')}
                value={diagnostics.device.hostReachable}
              />
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.communicationOk')}
                value={diagnostics.device.communicationOk}
              />
              {diagnostics.device.globalStatus ? (
                <Tap2PayDiagnosisRow
                  label={translate('tap2PayDiagnosis.labels.globalStatus')}
                  value={diagnostics.device.globalStatus}
                />
              ) : null}
              <Tap2PayDiagnosisRow
                label={translate('tap2PayDiagnosis.labels.androidVersion')}
                value={diagnostics.device.androidVersion}
              />

              <Text style={styles.sectionTitle}>
                {translate('tap2PayDiagnosis.sectionIssues')}
              </Text>
              {diagnostics.issues.length === 0 ? (
                <Tap2PayDiagnosisRow
                  label={translate('tap2PayDiagnosis.noIssues')}
                  value={true}
                />
              ) : (
                diagnostics.issues.map(issue => (
                  <View
                    testID="diagnosis-issue"
                    key={`diagnosis-issue-${issue.code}`}
                    style={styles.issue}
                  >
                    <Text style={styles.issueCode}>{issue.code}</Text>
                    <Text style={styles.issueText}>{issue.description}</Text>
                    <Text style={styles.issueAction}>{issue.action}</Text>
                  </View>
                ))
              )}

              {diagnostics.raw ? (
                <>
                  <Text style={styles.sectionTitle}>
                    {translate('tap2PayDiagnosis.sectionRaw')}
                  </Text>
                  <Text selectable testID="diagnosis-raw" style={styles.raw}>
                    {diagnostics.raw}
                  </Text>
                </>
              ) : null}
            </>
          ) : null}
        </ScrollView>
      </View>
    </View>
  );
};

export default Tap2PayDiagnosis;
